<!--
  Delete dialog for artifacts that declare `deletePreviewUrl`. Two states from
  one payload: blocked (why, and how to resolve it) or deletable (what else
  goes with it). The shared ConfirmationModal takes only a message string and
  has 21 callers, so this is separate rather than a prop on that.
-->
<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-200 p-4"
    @click.self="$emit('cancel')"
  >
    <div class="bg-white rounded-lg shadow-2xl max-w-lg w-full mx-auto overflow-hidden">
      <div class="bg-primary-100 px-6 py-4 flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">
          {{ blocked ? `Can’t delete this ${noun}` : `Delete this ${noun}?` }}
        </h3>
        <button class="text-gray-600 hover:text-gray-900" @click="$emit('cancel')">
          <font-awesome-icon :icon="['fas','times']" />
        </button>
      </div>

      <div class="px-6 py-5 max-h-[60vh] overflow-y-auto">
        <p class="text-sm text-gray-900 font-medium mb-1 break-words">{{ name }}</p>

        <div v-if="loading" class="py-6 text-center text-sm text-gray-500">
          <font-awesome-icon :icon="['fas','spinner']" spin class="mr-2" />Checking what depends on it…
        </div>

        <div v-else-if="loadError" class="mt-3 rounded-md bg-red-50 border border-red-200 px-4 py-3">
          <p class="text-sm text-red-dark">{{ loadError }}</p>
        </div>

        <template v-else-if="blocked">
          <p class="text-sm text-gray-600 mb-4">
            It is still in use. Resolve the following, then delete it.
          </p>
          <div
            v-for="b in preview.blockers"
            :key="b.key"
            class="mb-3 rounded-md bg-red-50 border border-red-200 px-4 py-3"
          >
            <p class="text-sm font-medium text-red-dark flex items-center gap-2">
              <font-awesome-icon :icon="['fas','circle-exclamation']" />
              {{ b.label }}
            </p>
            <p class="text-sm text-red-dark mt-1">{{ b.message }}</p>
            <p class="text-xs text-gray-600 mt-2">{{ b.resolution }}</p>
          </div>
        </template>

        <template v-else>
          <p class="text-sm text-gray-600">
            Nothing references it, so it is safe to remove. This cannot be undone.
          </p>
          <div v-if="preview.dependents && preview.dependents.length" class="mt-4">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Also affected
            </p>
            <ul class="border border-gray-200 rounded-md divide-y divide-gray-200">
              <li
                v-for="d in preview.dependents"
                :key="d.key"
                class="px-3 py-2 flex items-start justify-between gap-3"
              >
                <span class="text-sm text-gray-900">
                  {{ d.count }} {{ d.label }}
                  <span v-if="d.names" class="block text-xs text-gray-500 mt-0.5">
                    {{ d.names.join(', ') }}
                  </span>
                </span>
                <span
                  class="text-xs font-medium shrink-0 mt-0.5"
                  :class="d.effect === 'deleted' ? 'text-red-dark' : 'text-gray-600'"
                >
                  {{ d.effect === 'deleted' ? 'will be deleted' : 'will be unlinked' }}
                </span>
              </li>
            </ul>
          </div>
        </template>
      </div>

      <div class="px-6 pb-6 pt-2 flex gap-3">
        <button
          v-if="!blocked && !loading && !loadError"
          class="flex-1 bg-red-dark hover:bg-red-700 text-white font-medium py-2.5 px-6 rounded-lg transition-all duration-200 disabled:opacity-50"
          :disabled="deleting"
          @click="$emit('confirm')"
        >
          <font-awesome-icon :icon="['fas', deleting ? 'spinner' : 'check']" :spin="deleting" class="mr-2" />
          {{ deleting ? 'Deleting…' : 'Yes, delete it' }}
        </button>
        <button
          class="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-6 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-all duration-200"
          @click="$emit('cancel')"
        >
          {{ blocked ? 'Close' : 'Cancel' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeleteMetricModal',
  props: {
    isVisible: { type: Boolean, required: true },
    noun: { type: String, default: 'record' },
    name: { type: String, default: '' },
    preview: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    loadError: { type: String, default: '' },
    deleting: { type: Boolean, default: false },
  },
  emits: ['confirm', 'cancel'],
  computed: {
    blocked() {
      return !!(this.preview && this.preview.blockers && this.preview.blockers.length);
    },
  },
};
</script>
